import React, { createContext, useEffect, useState, useCallback, useContext } from "react";
import { formatDate } from "../utils/formatDate";
import { roomService, reservationService, blockedDateService, holidayDateService, firstReservationDiscountService, bookingConfigService } from "../services";
import { AuthContext } from "./AuthContext";
import {
  FaWifi,
  FaCoffee,
  FaParking,
  FaSwimmingPool,
  FaSnowflake,
  FaUtensils,
} from "react-icons/fa";

export const RoomContext = createContext();

const RoomProvider = ({ children }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const [rooms, setRooms] = useState([]);
  const [allRooms, setAllRooms] = useState([]); // Para almacenar todas las habitaciones de la API
  const [adults, setAdults] = useState("2 Adultos");
  const [kids, setKids] = useState("0 Niños");
  const [typeRoom, setTypeRoom] = useState("Hotel");
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [checkInDate, setCheckInDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [daysBetween, setDaysBetween] = useState(0);
  const [weekdaysCount, setWeekdaysCount] = useState(0);
  const [weekendCount, setWeekendCount] = useState(0);
  const [blockedDates, setBlockedDates] = useState([]); // Fechas bloqueadas desde la API
  const [firstReservationConfig, setFirstReservationConfig] = useState(null);
  const [isFirstReservation, setIsFirstReservation] = useState(false);
  const [holidayDates, setHolidayDates] = useState([]);
  const [bookingWindowDays, setBookingWindowDays] = useState(45); // Ventana de días reservables (configurable desde el dashboard)

  // Facilities por defecto para habitaciones de la API
  const getDefaultFacilities = () => [
    { name: "Wifi", icon: <FaWifi /> },
    { name: "Desayuno", icon: <FaCoffee /> },
    { name: "Estacionamiento", icon: <FaParking /> },
    { name: "Pileta exterior", icon: <FaSwimmingPool /> },
    { name: "Aire Acondicionado", icon: <FaSnowflake /> },
    { name: "Restaurante", icon: <FaUtensils /> },
  ];

  // Función para agregar campos de compatibilidad a las habitaciones de la API
  const enhanceRoomsFromAPI = useCallback((rooms) => {
    return rooms.map(room => ({
      ...room,
      // Para cards: usar imageCard, para detalles: usar images array
      imageRoom: room.images?.map(img => img.url) || (room.imageCard ? [room.imageCard] : []),
      facilities: getDefaultFacilities()
    }));
  }, []);

  // Cargar habitaciones y configuración de temporada desde la API al montar
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Cargar habitaciones, temporada activa, fechas bloqueadas y descuento primera reserva en paralelo
        const [roomsFromAPI, blockedDatesFromAPI, holidayDatesFromAPI, firstDiscountConfig, bookingConfig] = await Promise.all([
          roomService.getAllRoomsCards(),
          blockedDateService.getAll().catch(() => []),
          holidayDateService.getAll().catch(() => []),
          firstReservationDiscountService.getConfig().catch(() => null),
          bookingConfigService.getConfig().catch(() => null)
        ]);

        const enhancedRooms = enhanceRoomsFromAPI(roomsFromAPI);
        setAllRooms(enhancedRooms);
        setRooms(enhancedRooms);
        setBlockedDates(blockedDatesFromAPI);
        setHolidayDates(holidayDatesFromAPI);
        setFirstReservationConfig(firstDiscountConfig);
        if (bookingConfig?.bookingWindowDays > 0) {
          setBookingWindowDays(bookingConfig.bookingWindowDays);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setAllRooms([]);
        setRooms([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [enhanceRoomsFromAPI]);

  // Verificar elegibilidad de descuento de primera reserva cuando el usuario se loguea
  useEffect(() => {
    const checkFirstReservation = async () => {
      if (isAuthenticated && firstReservationConfig?.isActive) {
        try {
          const eligibility = await firstReservationDiscountService.checkEligibility();
          setIsFirstReservation(eligibility.isEligible);
        } catch (error) {
          console.error('Error checking first reservation eligibility:', error);
          setIsFirstReservation(false);
        }
      } else {
        setIsFirstReservation(false);
      }
    };

    checkFirstReservation();
  }, [isAuthenticated, firstReservationConfig]);

  useEffect(() => {
    const totalAdults = Number(adults[0]);
    const totalKids = Number(kids[0]);
    setTotal(totalAdults + totalKids);
  }, [adults, kids]);

  useEffect(() => {
    if (checkInDate && checkOutDate) {
      const startDate = new Date(checkInDate);
      const endDate = new Date(checkOutDate);
      const differenceInTime = endDate - startDate;

      const differenceInNights = Math.ceil(
        differenceInTime / (1000 * 3600 * 24)
      );
      setDaysBetween(differenceInNights);

      let weekdays = 0;
      let weekends = 0;
      let currentDate = new Date(startDate);

      for (let i = 0; i < differenceInNights; i++) {
        const dayOfWeek = currentDate.getDay(); // 0 = Domingo, 1 = Lunes, ..., 6 = Sábado
        const formattedDate = currentDate.toISOString().split("T")[0];
        const isSpecialWeekend = holidayDates.some(h => h.date.split("T")[0] === formattedDate);

        if (dayOfWeek >= 1 && dayOfWeek <= 4 && !isSpecialWeekend) {
          weekdays++; // Lunes a jueves (normal)
        } else {
          weekends++; // Viernes, Sábado, Domingo o fecha especial
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }

      setWeekdaysCount(weekdays);
      setWeekendCount(weekends);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInDate, checkOutDate, holidayDates]);

  // Calcular precio SIN descuentos (precio completo)
  const calculateFullPrice = (room, days, totalAdults, totalKids) => {
    if (days <= 0) return 0;

    let weekPrice = 0;
    let weekendPrice = 0;

    if (weekdaysCount > 0) {
      // Días de semana: usar price y priceDifference base
      const priceBaseWeek = room.price + (weekdaysCount - 1) * room.priceDifference;
      weekPrice = priceBaseWeek * totalAdults + priceBaseWeek * 0.5 * totalKids;
    }

    if (weekendCount > 0) {
      if (room.typeRoom === "GLAMPING") {
        // GLAMPING: usa precios separados para fin de semana
        const basePriceWeekend = room.priceWeekend || room.price;
        const diffWeekend = room.priceDifferenceWeekend || room.priceDifference;
        const priceBaseWeekend = basePriceWeekend + (weekendCount - 1) * diffWeekend;
        weekendPrice = priceBaseWeekend * totalAdults + priceBaseWeekend * 0.5 * totalKids;
      } else {
        // HOTEL: fin de semana usa el precio base
        const priceBaseWeekend = room.price + (weekendCount - 1) * room.priceDifference;
        weekendPrice = priceBaseWeekend * totalAdults + priceBaseWeekend * 0.5 * totalKids;
      }
    }

    return weekPrice + weekendPrice;
  };

  // Calcular precio CON descuentos de temporada
  const calculatePrice = (room, days, totalAdults, totalKids) => {
    if (days <= 0) return 0;

    let weekPrice = 0;
    let weekendPrice = 0;

    // Los descuentos por día se aplican en el servidor al confirmar la reserva.
    // Acá calculamos el precio base; el cliente final se valida en POST /reservation.
    if (weekdaysCount > 0) {
      const priceBaseWeek = room.price + (weekdaysCount - 1) * room.priceDifference;
      weekPrice = priceBaseWeek * totalAdults + priceBaseWeek * 0.5 * totalKids;
    }

    if (weekendCount > 0) {
      if (room.typeRoom === "GLAMPING") {
        // GLAMPING: usa precios separados para fin de semana
        const basePriceWeekend = room.priceWeekend || room.price;
        const diffWeekend = room.priceDifferenceWeekend || room.priceDifference;
        const priceBaseWeekend = basePriceWeekend + (weekendCount - 1) * diffWeekend;
        weekendPrice = priceBaseWeekend * totalAdults + priceBaseWeekend * 0.5 * totalKids;
      } else {
        // HOTEL: fin de semana usa precio base sin descuento
        const priceBaseWeekend = room.price + (weekendCount - 1) * room.priceDifference;
        weekendPrice = priceBaseWeekend * totalAdults + priceBaseWeekend * 0.5 * totalKids;
      }
    }

    return weekPrice + weekendPrice;
  };

  const handleClick = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const totalAdults = Number(adults[0]);
      const totalKids = Number(kids[0]);

      // Mapear typeRoom del frontend al enum de la API
      const apiTypeRoom = typeRoom === "Hotel" ? "HOTEL" : "GLAMPING";

      let roomsToProcess;

      // Si tenemos fechas, verificar disponibilidad con la API
      if (checkInDate && checkOutDate) {
        try {
          const availabilityResponse = await reservationService.checkAvailability(
            checkInDate,
            checkOutDate,
            totalAdults,
            totalKids
          );

          // Usar solo las habitaciones que la API confirma disponibles.
          // Si no hay ninguna (o falla la consulta), no mostrar nada:
          // nunca ofrecer habitaciones que pueden estar ocupadas.
          if (availabilityResponse.availableRooms && availabilityResponse.availableRooms.length > 0) {
            roomsToProcess = enhanceRoomsFromAPI(availabilityResponse.availableRooms);
          } else {
            roomsToProcess = [];
          }
        } catch (error) {
          console.error('Error checking availability:', error);
          roomsToProcess = [];
        }
      } else {
        // Sin fechas, usar todas las habitaciones
        roomsToProcess = allRooms;
      }

      // Filtrar por tipo de habitación y capacidad
      const candidates = roomsToProcess.filter((room) => {
        if (room.typeRoom !== apiTypeRoom) return false;
        if (!total || total === 0) return true;
        if (Array.isArray(room.maxPerson)) {
          return room.maxPerson.includes(total);
        }
        return total <= room.maxPerson;
      });

      // Si hay fechas, pedimos el precio real al server por cada cuarto (incluye overrides por día).
      // Si no hay fechas, usamos el precio base local.
      const hasDates = !!(checkInDate && checkOutDate && daysBetween > 0);
      const filteredRooms = await Promise.all(candidates.map(async (room) => {
        let warningMessage = "";
        if (room.minTwoNightsWeekend && weekendCount > 0 && daysBetween < 2) {
          warningMessage = "Mínimo 2 noches los fines de semana";
        }

        const localBase = calculatePrice(room, daysBetween, totalAdults, totalKids);
        const fullPrice = calculateFullPrice(room, daysBetween, totalAdults, totalKids);

        let serverTotal = null;
        let serverDiscount = 0;
        if (hasDates) {
          try {
            const res = await reservationService.calculatePrice({
              roomId: room.id,
              checkIn: checkInDate,
              checkOut: checkOutDate,
              adults: totalAdults,
              children: totalKids,
            });
            serverTotal = res.total;
            serverDiscount = res.discount || 0;
          } catch (err) {
            console.error('calculatePrice failed for room', room.id, err);
          }
        }

        const pricePerNight = serverTotal !== null ? serverTotal : localBase;
        const seasonDiscount = fullPrice > 0
          ? Math.round((serverDiscount / fullPrice) * 100)
          : 0;

        // Aplicar descuento de primera reserva sobre el precio ya descontado
        let firstReservationDiscountPercent = 0;
        let firstReservationDiscountAmount = 0;
        let finalPrice = pricePerNight;

        if (isFirstReservation && firstReservationConfig?.isActive && pricePerNight > 0) {
          firstReservationDiscountPercent = firstReservationConfig.discountPercent;
          firstReservationDiscountAmount = Math.round(pricePerNight * (firstReservationDiscountPercent / 100));
          finalPrice = pricePerNight - firstReservationDiscountAmount;
        }

        return {
          ...room,
          totalAdults,
          totalKids,
          checkInDate: formatDate(checkInDate),
          checkOutDate: formatDate(checkOutDate),
          daysBetween,
          weekdaysCount,
          weekendCount,
          totalPrice: finalPrice,
          fullPrice,
          warningMessage,
          seasonDiscount,
          firstReservationDiscount: firstReservationDiscountPercent,
          firstReservationDiscountAmount,
          priceBeforeFirstDiscount: pricePerNight,
        };
      }));

      const cleanRooms = filteredRooms.filter((room) => room !== null);
      setRooms(cleanRooms);
      return cleanRooms;

    } catch (error) {
      console.error('Error in handleClick:', error);
      setRooms([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  return (
    <RoomContext.Provider
      value={{
        rooms,
        adults,
        setAdults,
        kids,
        setKids,
        handleClick,
        loading,
        typeRoom,
        setTypeRoom,
        checkInDate,
        setCheckInDate,
        checkOutDate,
        setCheckOutDate,
        daysBetween,
        weekdaysCount,
        weekendCount,
        blockedDates,
        firstReservationConfig,
        isFirstReservation,
        bookingWindowDays,
      }}
    >
      {children}
    </RoomContext.Provider>
  );
};

export default RoomProvider;
